import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating, useFocus, useInteractions, FloatingFocusManager } from '@floating-ui/react';
import { KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { Icon, IconNames, Text, Tooltip } from '@sendible/design-system';
import { useInView } from 'react-intersection-observer';
import { Container, Labels, List, Option, Spacer } from './index.styles';

export interface OptionItem {
  disabled?: boolean;
  extraLabel?: string;
  icon?: IconNames;
  id: string;
  label: string;
  testId: string;
  tooltip?: string;
}

interface Props {
  buttonId: string;
  isVisible: boolean;
  onClose: () => void;
  onSelect: (option: string) => void;
  options: OptionItem[];
  portal: HTMLElement | null;
}

export const ComposeToolbarDropdown = ({ buttonId, isVisible, onClose, onSelect, options, portal }: Props) => {
  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    rootMargin: '-8px',
  });

  const { x, y, strategy, refs, context } = useFloating({
    open: isVisible,
    onOpenChange: onClose,
    placement: 'bottom-start',
    middleware: [
      offset({ mainAxis: 0, alignmentAxis: -4, crossAxis: 0 }),
      shift(),
      flip({ fallbackPlacements: ['top-start', 'right-end', 'right-start'] }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const focus = useFocus(context);
  const setFloatingRef = useCallback(
    (node: HTMLUListElement | null) => {
      refs.setFloating(node);
    },
    [refs]
  );

  const setReferenceRef = useCallback(
    (node: HTMLDivElement | null) => {
      refs.setReference(node);
    },
    [refs]
  );
  const { getReferenceProps } = useInteractions([focus]);

  const handleClick = (option: string) => {
    onSelect(option);
    onClose();
  };

  const handleKeyDown = (key: string, index: number) => {
    const { previousSibling } = document.activeElement;
    const { nextSibling } = document.activeElement;

    if (key === 'ArrowUp' && index > 0) {
      if (previousSibling.tabIndex === 0) previousSibling.focus();
      else previousSibling.previousSibling.focus();

      return;
    }
    if (key === 'ArrowDown' && index < options.length - 1) {
      if (nextSibling.tabIndex === 0) nextSibling.focus();
      else nextSibling.nextSibling.focus();

      return;
    }
    if (key === 'Escape') return onClose();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBlur = (e: any) => {
    const relatedTargetId = e?.relatedTarget?.id;

    if (relatedTargetId) {
      if (relatedTargetId === buttonId) return;

      const listOfIdsToIgnoreBlur = options.map(({ id }) => id);

      if (listOfIdsToIgnoreBlur.includes(e.relatedTarget.id)) return;
    }

    onClose();
  };

  // This effect always fires once right after mount (regardless of the dependency array)
  // to report inView's initial value, which the IntersectionObserver hasn't necessarily
  // had a chance to settle by then - on cold mounts where isVisible is already true on the
  // very first render (e.g. a caller that opens the compose box and the dropdown in the
  // same action), inView briefly reads its default `false`, and this would otherwise close
  // the dropdown again immediately, before it's ever visibly shown. Skip that first firing;
  // only react once inView has actually changed after mount (e.g. scrolled out of view).
  const hasSeenInitialInViewRef = useRef(false);

  useEffect(() => {
    if (!hasSeenInitialInViewRef.current) {
      hasSeenInitialInViewRef.current = true;

      return;
    }

    if (isVisible && !inView) onClose();
  }, [inView]);

  // Callers look the anchor up by id, and it isn't always in the DOM - e.g. stream-item
  // replies (Tasks / Inbox "Reply") open a compose modal without the compose toolbar. A null
  // createPortal target throws, and with no error boundary that unmounts the whole app.
  const dropdownRoot = document.getElementById('dropdown-root');

  if (!portal) return null;

  return (
    <>
      {createPortal(
        <div ref={inViewRef}>
          <Container
            ref={setReferenceRef}
            {...getReferenceProps()}
          />
        </div>,
        portal
      )}
      {isVisible &&
        dropdownRoot &&
        createPortal(
          <FloatingFocusManager
            context={context}
            initialFocus={-1}
          >
            <List
              ref={setFloatingRef}
              style={{ position: strategy, top: y || 0, left: x || 0 }}
              onBlur={handleBlur}
              tabIndex={0}
            >
              {options.map(({ disabled, extraLabel, icon, id, label, testId, tooltip }, index) => (
                <Tooltip
                  content={tooltip}
                  key={id}
                  noDecoration
                >
                  <Option
                    className={id}
                    data-testid={testId}
                    disabled={disabled}
                    $hasExtraLabel={!!extraLabel}
                    id={id}
                    onClick={() => handleClick(id)}
                    onKeyDown={({ code }: KeyboardEvent<HTMLElement>) => handleKeyDown(code, index)}
                    type="button"
                  >
                    {icon && (
                      <Icon
                        color="brandDark"
                        name={icon}
                      />
                    )}
                    <Labels>
                      <Text variation="body_14">{label}</Text>
                      {extraLabel && (
                        <Text
                          variation="body_12"
                          color="brandDarkTint30"
                        >
                          {extraLabel}
                        </Text>
                      )}
                    </Labels>
                  </Option>
                  {extraLabel && <Spacer tabIndex={-1} />}
                </Tooltip>
              ))}
            </List>
          </FloatingFocusManager>,
          dropdownRoot
        )}
    </>
  );
};
